/**
 * Places arranged into the sections a plan is actually made of.
 *
 * A trip is decided in a fixed order — where you sleep, then what you do — and a flat list of thirty
 * places in the order they were saved answers none of it. This groups the picker by intent so the user
 * can see at a glance whether they have somewhere to stay, and whether their cafes outnumber their
 * sights.
 *
 * **Each place lands in exactly one section**, and that is the rule this module exists to get right. A
 * place carries a set of categories, so "show it under each" would draw the same row twice and make the
 * count wrong in the header — and a selection made on the second copy would look like it failed on the
 * first. The tie is broken by the section order below: the first section whose categories intersect the
 * place's wins. The row prints the place's own categories, so a multi-category place is still visibly
 * multi-category rather than hidden behind the one section it was filed under.
 *
 * The section order is deliberately not `categoryChoices`' order. That one is the capture form's, and it
 * starts at `cafe` because that is the most common thing to save. This one starts at `stay`, because
 * this is where a trip is planned.
 */

import type { PlaceCategory } from "@trippool/contracts";

export interface PlaceGroupDefinition {
  key: string;
  label: string;
  glyph: string;
  /** The categories this section claims. Empty means the catch-all, which must stay last. */
  categories: readonly PlaceCategory[];
}

export const PLACE_GROUPS: readonly PlaceGroupDefinition[] = [
  { key: "stay", label: "Lưu trú", glyph: "🛏️", categories: ["stay"] },
  { key: "cafe", label: "Cà phê", glyph: "☕", categories: ["cafe"] },
  { key: "food", label: "Ăn uống", glyph: "🍜", categories: ["restaurant", "dessert"] },
  { key: "see", label: "Tham quan / vui chơi", glyph: "📷", categories: ["attraction", "nature"] },
  { key: "shop", label: "Mua sắm / chợ", glyph: "🧺", categories: ["market", "shopping"] },
  { key: "move", label: "Di chuyển", glyph: "🚌", categories: ["transport"] },
  /*
   * The catch-all, and it has to be last for the first-match rule to work.
   *
   * It carries `other` as well as the empty case, so a place the user explicitly marked "khác" and a
   * place they never classified are shown together — they are the same fact to a planner looking for
   * something to do, and a separate section for one or two rows each would be two headings saying
   * "unfinished".
   */
  { key: "unsorted", label: "Chưa phân loại", glyph: "📍", categories: ["other"] },
];

export interface GroupedPlaces<T> {
  group: PlaceGroupDefinition;
  places: T[];
}

/**
 * The place's own section: the first one that claims any of its categories.
 *
 * Exported because the row needs it too — a place with several categories prints the ones the section
 * does not account for, which is what keeps the single-section rule from hiding information.
 */
export function groupOf(categories: readonly PlaceCategory[]): PlaceGroupDefinition {
  const claimed = PLACE_GROUPS.find(
    (group) => group.categories.length > 0 && group.categories.some((category) => categories.includes(category)),
  );
  // Unreachable while the last entry is the catch-all with an empty `categories`; typed as a real
  // fallback so a future edit that reorders the table degrades to "unsorted" rather than to nothing.
  return claimed ?? PLACE_GROUPS[PLACE_GROUPS.length - 1]!;
}

/**
 * The section a place's area puts it in.
 *
 * Areas are the other way a user thinks about their own places — "what have I got in Đà Lạt" is a
 * different question from "what have I got to eat" — and the screen offers both, so this is the same
 * first-match discipline one level up: a place filed under several areas lands in its **first** one, so
 * the section counts stay honest and no row is drawn twice.
 */
export interface AreaGroup<T> {
  /** The area's id, or `UNASSIGNED_AREA_KEY` for the ones filed under none. */
  key: string;
  /** The area's name, or the label for the unassigned section. */
  name: string;
  places: T[];
}

/** The key of the section holding places that are in no area. */
export const UNASSIGNED_AREA_KEY = "__unassigned__";

export interface PlaceArea {
  id: string;
  name: string;
}

/**
 * Groups places by the area they are filed under, biggest section first.
 *
 * Biggest first rather than alphabetical, and that is the whole ordering rule: it is what makes the
 * screen open on the section the user actually has places in, and it keeps the most useful area one tap
 * away instead of behind three collapsed ones. Two sections of the same size fall back to their names,
 * so the order is stable rather than dependent on the order places were saved.
 *
 * The unassigned section always sorts last whatever its size: it is not somewhere the user chose, and
 * putting it first would open the screen on the least finished part of the list.
 */
export function groupPlacesByArea<T extends { areas: readonly PlaceArea[] }>(
  places: readonly T[],
): Array<AreaGroup<T>> {
  const sections = new Map<string, AreaGroup<T>>();
  for (const place of places) {
    const area = place.areas[0];
    const key = area?.id ?? UNASSIGNED_AREA_KEY;
    const existing = sections.get(key);
    if (existing !== undefined) {
      existing.places.push(place);
      continue;
    }
    sections.set(key, { key, name: area?.name ?? "Chưa xếp khu vực", places: [place] });
  }

  return [...sections.values()].sort((a, b) => {
    const unassigned = Number(a.key === UNASSIGNED_AREA_KEY) - Number(b.key === UNASSIGNED_AREA_KEY);
    if (unassigned !== 0) return unassigned;
    return b.places.length - a.places.length || a.name.localeCompare(b.name, "vi");
  });
}

/**
 * Groups places, keeping the input order inside each section and dropping empty ones.
 *
 * Empty sections are dropped rather than rendered with "0 địa điểm": a heading for a kind of place the
 * user has none of is a list of what is missing, which is not what this screen is for.
 */
export function groupPlaces<T extends { categories: readonly PlaceCategory[] }>(
  places: readonly T[],
): Array<GroupedPlaces<T>> {
  return PLACE_GROUPS.map((group) => ({
    group,
    places: places.filter((place) => groupOf(place.categories).key === group.key),
  })).filter((grouped) => grouped.places.length > 0);
}
